import {
    BathroomOptions,
    BedroomOptions,
    CarParkOptions,
    LotSizeOptions,
    PropertyStyles,
    PropertyTypes,
    PurchaseTimeframes,
    RenovationPreferences,
    SettlementTerms,
    WishlistSchema,
    YesNoUnsure,
} from '@buyerflo/core/schemas/purchaser'
import { FormSection } from '@buyerflo/ui/components/FormSection'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import Checkbox from 'components/checkbox'
import { SlotInput, SlotTextArea } from 'components/input'
import { Label } from 'components/label'
import { SlotMultiCheckbox } from 'components/multiCheckbox'
import { SlotSingleSelectCheckboxGroup } from 'components/singleSelectCheckboxGroup'
import Select, { Option } from 'components/select'
import { useShowSnack } from 'components/snackbar'
import { forwardRef, useEffect, useImperativeHandle } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { SizableText, View, XStack, YStack } from 'tamagui'
import { useTRPC } from 'utils/trpc'
import { usePurchasersApi } from 'apiProviders'
import { useEditMode } from './editMode'
import { CLIENT_DETAIL_MAX_WIDTH } from './helpers'
import { usePurchaserContext } from './purchaserProvider'

/**
 * Two-column field row. Stacks to a single column below the `md` breakpoint.
 *
 * Note: this config's `$sm` is a min-width (mobile-first) query, so we default
 * to a stacked column and only switch to two columns at `$md` and up.
 */
const Row = ({ children }: { children: React.ReactNode }) => (
    <XStack
        flexDirection="column"
        gap="$3.5"
        $md={{ flexDirection: 'row', gap: '$4' }}
    >
        {children}
    </XStack>
)

/**
 * Equal-width cell within a {@link Row}. `flexBasis: 0` is applied only at `$md`
 * (when the row is horizontal); while stacked it must stay `auto` or the cell
 * collapses to zero height and its contents overlap the fields below.
 */
const Cell = ({ children }: { children: React.ReactNode }) => (
    <YStack
        minWidth={0}
        $md={{ flexBasis: 0, flexGrow: 1, flexShrink: 1 }}
    >
        {children}
    </YStack>
)

export const EditForm = ({ control, hideContactInfo }: { control: any; hideContactInfo?: boolean }) => (
    <YStack
        gap="$4"
        maxWidth={CLIENT_DETAIL_MAX_WIDTH}
        paddingBottom="$8"
    >
        {/* Contact Information */}
        {!hideContactInfo && (
            <FormSection title="Contact Information">
                <Row>
                    <Cell>
                        <Controller
                            name="firstName"
                            control={control}
                            render={({ field, fieldState, formState }) => (
                                <SlotInput
                                    label="First Name"
                                    field={field}
                                    fieldState={fieldState}
                                    formState={formState}
                                />
                            )}
                        />
                    </Cell>
                    <Cell>
                        <Controller
                            name="lastName"
                            control={control}
                            render={({ field, fieldState, formState }) => (
                                <SlotInput
                                    label="Last Name"
                                    field={field}
                                    fieldState={fieldState}
                                    formState={formState}
                                />
                            )}
                        />
                    </Cell>
                </Row>

                <Controller
                    name="email"
                    control={control}
                    render={({ field, fieldState, formState }) => (
                        <SlotInput
                            label="Email"
                            type="email"
                            field={field}
                            fieldState={fieldState}
                            formState={formState}
                        />
                    )}
                />

                <Controller
                    name="signUpForUpdates"
                    control={control}
                    render={({ field }) => (
                        <Checkbox
                            label="Sign up for news and updates"
                            checked={field.value ?? false}
                            onChange={e => field.onChange(e.target.checked)}
                        />
                    )}
                />

                <Controller
                    name="postalAddress"
                    control={control}
                    render={({ field, fieldState, formState }) => (
                        <SlotInput
                            label="Postal Address"
                            field={field}
                            fieldState={fieldState}
                            formState={formState}
                        />
                    )}
                />

                <Controller
                    name="contactPerson"
                    control={control}
                    render={({ field, fieldState, formState }) => (
                        <SlotInput
                            label="Contact Person"
                            field={field}
                            fieldState={fieldState}
                            formState={formState}
                        />
                    )}
                />

                <Controller
                    name="preferredCommunication"
                    control={control}
                    render={({ field, formState }) => (
                        <SlotMultiCheckbox
                            field={field}
                            formState={formState}
                            label="Preferred Communication"
                            options={['Text', 'Phone Call', 'WhatsApp Chat', 'Email']}
                        />
                    )}
                />

                <Controller
                    name="bestContactTimes"
                    control={control}
                    render={({ field, fieldState, formState }) => (
                        <SlotInput
                            label="Best Contact Times"
                            field={field}
                            fieldState={fieldState}
                            formState={formState}
                        />
                    )}
                />
            </FormSection>
        )}

        {/* Purchase Details */}
        <FormSection title="Purchase Details">
            <Controller
                name="purchaseEntity"
                control={control}
                render={({ field, fieldState, formState }) => (
                    <SlotInput
                        label="Purchase Entity"
                        field={field}
                        fieldState={fieldState}
                        formState={formState}
                    />
                )}
            />

            <Controller
                name="subjectToFIRB"
                control={control}
                render={({ field, formState }) => (
                    <SlotSingleSelectCheckboxGroup
                        field={field}
                        formState={formState}
                        label="Subject to FIRB"
                        options={YesNoUnsure}
                    />
                )}
            />

            <Controller
                name="budgetRange"
                control={control}
                render={({ field, fieldState, formState }) => (
                    <SlotInput
                        label="Budget Range"
                        field={field}
                        fieldState={fieldState}
                        formState={formState}
                    />
                )}
            />

            <Row>
                <Cell>
                    <Label>Settlement Terms</Label>
                    <Controller
                        name="settlementTerms"
                        control={control}
                        render={({ field }) => (
                            <Select
                                placeholder="Select settlement terms"
                                value={field.value ?? null}
                                onChange={(_: any, value: any) => field.onChange(value)}
                            >
                                {SettlementTerms.map(term => (
                                    <Option
                                        key={term}
                                        value={term}
                                    >
                                        {term}
                                    </Option>
                                ))}
                            </Select>
                        )}
                    />
                </Cell>
                <Cell>
                    <Label>Purchase Timeframe</Label>
                    <Controller
                        name="purchaseTimeframe"
                        control={control}
                        render={({ field }) => (
                            <Select
                                placeholder="Select purchase timeframe"
                                value={field.value ?? null}
                                onChange={(_: any, value: any) => field.onChange(value)}
                            >
                                {PurchaseTimeframes.map(timeframe => (
                                    <Option
                                        key={timeframe}
                                        value={timeframe}
                                    >
                                        {timeframe}
                                    </Option>
                                ))}
                            </Select>
                        )}
                    />
                </Cell>
            </Row>
        </FormSection>

        {/* Property Preferences */}
        <FormSection title="Property Preferences">
            <Controller
                name="propertyTypes"
                control={control}
                render={({ field, formState }) => (
                    <SlotMultiCheckbox
                        field={field}
                        formState={formState}
                        label="Property Types"
                        options={PropertyTypes}
                    />
                )}
            />

            <Controller
                name="propertyStyles"
                control={control}
                render={({ field, formState }) => (
                    <SlotMultiCheckbox
                        field={field}
                        formState={formState}
                        label="Property Styles"
                        options={PropertyStyles}
                    />
                )}
            />

            <YStack>
                <Label>Renovation Preference</Label>
                <Controller
                    name="renovationPreference"
                    control={control}
                    render={({ field }) => (
                        <Select
                            placeholder="Select preference"
                            value={field.value ?? null}
                            onChange={(_: any, value: any) => field.onChange(value)}
                        >
                            {RenovationPreferences.map(pref => (
                                <Option
                                    key={pref}
                                    value={pref}
                                >
                                    {pref}
                                </Option>
                            ))}
                        </Select>
                    )}
                />
            </YStack>

            <Controller
                name="wantPool"
                control={control}
                render={({ field, formState }) => (
                    <SlotSingleSelectCheckboxGroup
                        field={field}
                        formState={formState}
                        label="Want Pool"
                        options={YesNoUnsure}
                    />
                )}
            />

            <Row>
                <Cell>
                    <Label>Bedrooms</Label>
                    <Controller
                        name="bedrooms"
                        control={control}
                        render={({ field }) => (
                            <Select
                                placeholder="Select bedrooms"
                                value={field.value ?? null}
                                onChange={(_: any, value: any) => field.onChange(value)}
                            >
                                {BedroomOptions.map(option => (
                                    <Option
                                        key={option}
                                        value={option}
                                    >
                                        {option}
                                    </Option>
                                ))}
                            </Select>
                        )}
                    />
                </Cell>
                <Cell>
                    <Label>Bathrooms</Label>
                    <Controller
                        name="bathrooms"
                        control={control}
                        render={({ field }) => (
                            <Select
                                placeholder="Select bathrooms"
                                value={field.value ?? null}
                                onChange={(_: any, value: any) => field.onChange(value)}
                            >
                                {BathroomOptions.map(option => (
                                    <Option
                                        key={option}
                                        value={option}
                                    >
                                        {option}
                                    </Option>
                                ))}
                            </Select>
                        )}
                    />
                </Cell>
            </Row>

            <Row>
                <Cell>
                    <Label>Car Parks</Label>
                    <Controller
                        name="carParks"
                        control={control}
                        render={({ field }) => (
                            <Select
                                placeholder="Select car parks"
                                value={field.value ?? null}
                                onChange={(_: any, value: any) => field.onChange(value)}
                            >
                                {CarParkOptions.map(option => (
                                    <Option
                                        key={option}
                                        value={option}
                                    >
                                        {option}
                                    </Option>
                                ))}
                            </Select>
                        )}
                    />
                </Cell>
                <Cell>
                    <Label>Preferred Lot Size</Label>
                    <Controller
                        name="preferredLotSize"
                        control={control}
                        render={({ field }) => (
                            <Select
                                placeholder="Select lot size"
                                value={field.value ?? null}
                                onChange={(_: any, value: any) => field.onChange(value)}
                            >
                                {LotSizeOptions.map(option => (
                                    <Option
                                        key={option}
                                        value={option}
                                    >
                                        {option}
                                    </Option>
                                ))}
                            </Select>
                        )}
                    />
                </Cell>
            </Row>
        </FormSection>

        {/* Location & Requirements */}
        <FormSection title="Location & Requirements">
            <Controller
                name="targetSuburbs"
                control={control}
                render={({ field, formState }) => (
                    <SlotTextArea
                        label="Target Suburbs"
                        minRows={2}
                        field={field}
                        formState={formState}
                    />
                )}
            />

            <Controller
                name="schoolsImportant"
                control={control}
                render={({ field, fieldState, formState }) => (
                    <SlotInput
                        label="Schools Important"
                        field={field}
                        fieldState={fieldState}
                        formState={formState}
                    />
                )}
            />

            <Controller
                name="dealBreakers"
                control={control}
                render={({ field, formState }) => (
                    <SlotTextArea
                        label="Deal Breakers"
                        minRows={2}
                        field={field}
                        formState={formState}
                    />
                )}
            />

            <Controller
                name="detailedDescription"
                control={control}
                render={({ field, formState }) => (
                    <SlotTextArea
                        label="Detailed Description"
                        minRows={4}
                        field={field}
                        formState={formState}
                    />
                )}
            />
        </FormSection>

        {/* Property Interests */}
        <FormSection title="Property Interests">
            <Controller
                name="specificProperties"
                control={control}
                render={({ field, formState }) => (
                    <SlotTextArea
                        label="Specific Properties"
                        minRows={2}
                        field={field}
                        formState={formState}
                    />
                )}
            />

            <Controller
                name="currentListingsInterest"
                control={control}
                render={({ field, formState }) => (
                    <SlotTextArea
                        label="Current Listings of Interest"
                        minRows={2}
                        field={field}
                        formState={formState}
                    />
                )}
            />
        </FormSection>

        {/* Professional Contacts */}
        <FormSection title="Professional Contacts">
            <Controller
                name="solicitorDetails"
                control={control}
                render={({ field, formState }) => (
                    <SlotTextArea
                        label="Solicitor Details"
                        minRows={2}
                        field={field}
                        formState={formState}
                    />
                )}
            />

            <Controller
                name="bankerBrokerDetails"
                control={control}
                render={({ field, formState }) => (
                    <SlotTextArea
                        label="Banker / Mortgage Broker Details"
                        minRows={2}
                        field={field}
                        formState={formState}
                    />
                )}
            />
        </FormSection>
    </YStack>
)

const getWishlistDefaults = (w?: WishlistSchema) =>
    ({
        preferredCommunication: [],
        propertyTypes: [],
        propertyStyles: [],
        ...w,
    }) as WishlistSchema

const cleanWishlistValues = (data: Record<string, any>) =>
    Object.fromEntries(
        Object.entries(data).filter(([_, v]) => {
            if (v === '' || v === undefined || v === null) return false
            if (Array.isArray(v) && v.length === 0) return false
            if (v === false) return false
            return true
        }),
    )

const WishlistView = forwardRef(({}, ref: any) => {
    const trpc = useTRPC()
    const { purchaser, setPurchaser } = usePurchaserContext()
    const { editMode } = useEditMode()
    const updateWishlistMutation = useMutation(trpc.purchaser.updateWishlist.mutationOptions())
    const createPurchaserMutation = useMutation(trpc.purchaser.createPurchaser.mutationOptions())
    const showSnack = useShowSnack()
    const navigate = useNavigate()
    const purchasersApi = usePurchasersApi(false)
    const queryClient = useQueryClient()
    const wishlist = purchaser.wishlist
    const isNew = purchaser.purchaserId == undefined

    // Saving the wishlist writes a new audit log entry server-side, so mark the
    // cached audit log stale to force a refetch next time the tab is viewed.
    const invalidateAuditLog = () =>
        queryClient.invalidateQueries({
            queryKey: trpc.purchaser.getAuditLog.queryKey({ purchaserId: purchaser.purchaserId }),
        })

    const { handleSubmit, control, reset, getValues } = useForm<WishlistSchema>({
        resolver: isNew ? undefined : zodResolver(WishlistSchema),
        defaultValues: getWishlistDefaults(purchaser.wishlist),
    })

    // Persist form state to context on unmount when creating a new client
    useEffect(() => {
        return () => {
            if (isNew) {
                const values = getValues()
                const cleaned = cleanWishlistValues(values)
                if (Object.keys(cleaned).length > 0) {
                    setPurchaser(prev => ({ ...prev, wishlist: cleaned as WishlistSchema }))
                }
            }
        }
    }, [])

    const saveForm = async (): Promise<boolean> => {
        if (isNew) {
            // Validate that client data was filled in on the Client tab
            if (!purchaser.buyers?.length || !purchaser.agents?.length || (purchaser as any).commission == undefined) {
                showSnack('Please fill in buyer and agent details on the Client tab first', 'danger')
                return false
            }

            // Build wishlist from form values
            const data = getValues()
            const cleaned = cleanWishlistValues(data)
            const firstBuyer = purchaser.buyers[0]
            const wishlistData = Object.keys(cleaned).length > 0
                ? {
                    ...cleaned,
                    firstName: firstBuyer.firstName,
                    lastName: firstBuyer.lastName,
                    email: firstBuyer.email,
                } as WishlistSchema
                : undefined

            try {
                const createdPurchaser = await createPurchaserMutation.mutateAsync({
                    agents: purchaser.agents,
                    buyers: purchaser.buyers,
                    commission: (purchaser as any).commission,
                    commissionType: (purchaser as any).commissionType,
                    commissionIncEx: (purchaser as any).commissionIncEx,
                    purchaserStatus: purchaser.purchaserStatus,
                    authorityStartDate: (purchaser as any).authorityStartDate,
                    authorityPeriod: (purchaser as any).authorityPeriod,
                    additionalDetails: purchaser.additionalDetails,
                    wishlist: wishlistData,
                } as any)
                await purchasersApi.refresh()
                navigate(`../../${createdPurchaser.purchaserId}`, {
                    relative: 'path',
                    replace: true,
                })
                showSnack('Client saved', 'success')
                return true
            } catch (e) {
                console.error(e)
                showSnack('Error saving client', 'danger')
                return false
            }
        }

        return new Promise(resolve => {
            handleSubmit(
                async data => {
                    try {
                        const cleaned = cleanWishlistValues(data)
                        const updatedPurchaser = await updateWishlistMutation.mutateAsync({
                            purchaserId: purchaser.purchaserId,
                            wishlist: cleaned as WishlistSchema,
                        })
                        setPurchaser(updatedPurchaser)
                        invalidateAuditLog()
                        resolve(true)
                    } catch {
                        resolve(false)
                    }
                },
                () => {
                    resolve(false)
                },
            )()
        })
    }

    useImperativeHandle(ref, () => ({
        saveForm,
        resetForm: () => reset(getWishlistDefaults(purchaser.wishlist)),
    }))

    if (!editMode && !wishlist) {
        return (
            <View
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight={300}
            >
                <SizableText
                    fontSize="$4"
                    color="$colorPress"
                >
                    No wishlist information available for this client.
                </SizableText>
            </View>
        )
    }

    return (
        <View paddingBottom="$6">
            <EditForm control={control} hideContactInfo={isNew} />
        </View>
    )
})

export default WishlistView
